import { IDropdownOption, IStyle, MessageBarType, Stack } from '@fluentui/react';
import {
  BaseDialog,
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  buttonStylesStealth,
  IconButton,
  IH2OTheme,
  Info,
  TextField,
  TextWithCopy,
  textWithCopyStylesBorder,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';

import { Endpoint } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/endpoint_pb';
import {
  useMutationCreateEndpoint,
  useMutationDeleteEndpoint,
  useMutationUpdateEndpoint,
  useQueryEndpoints,
} from '../../mlops/mlops.deployer.hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatError } from '../../utils/utils';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { useGranularMLOpsPermissions } from './userActions';

export interface IDeploymentTabEndpointsProps {
  isNew: boolean;
  scorerEndpoint?: string;
  deploymentId?: string;
  deploymentEnvironmentId?: string;
  workspaceId: string;
}

interface IDeploymentTabEndpointsStyles {
  form: IStyle;
  dialogInput: IStyle;
  customEndpointButton: IStyle;
}

const deploymentTabEndpointsStyles = (theme: IH2OTheme): IDeploymentTabEndpointsStyles => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 20,
    margin: '0px 10px',
    // TODO: Remove once theme is used.
    borderColor: theme.semanticColors?.bodyBackground,
  },
  dialogInput: {
    width: '30%',
    minWidth: 360,
    maxWidth: 460,
    marginRight: 12,
  },
  customEndpointButton: {
    margin: '6px 0',
  },
});

export const DeploymentTabEndpoints = ({
  isNew,
  scorerEndpoint,
  deploymentId,
  workspaceId,
}: IDeploymentTabEndpointsProps) => {
  const theme = useTheme(),
    classNames = useClassNames<IDeploymentTabEndpointsStyles, ClassNamesFromIStyles<IDeploymentTabEndpointsStyles>>(
      'deploymentTabEndpoints',
      deploymentTabEndpointsStyles(theme)
    ),
    { showDialog } = useConfirmDialog(),
    [endpointOptions, setEndpointOptions] = React.useState<IDropdownOption[]>([]),
    [endpointPrefix, setEndpointPrefix] = React.useState(''),
    [endpointSuffix, setEndpointSuffix] = React.useState(''),
    [customError, setCustomError] = React.useState<string | undefined>(undefined),
    [dialogVisible, setDialogVisible] = React.useState(false),
    [showValidation, setShowValidation] = React.useState(false),
    [customEndpointPath, setCustomEndpointPath] = React.useState(''),
    [endpointName, setEndpointName] = React.useState(''),
    [endpointDescription, setEndpointDescription] = React.useState(''),
    [deploymentTarget, setDeploymentTarget] = React.useState(''),
    [currentlyEditedEndpointName, setCurrentlyEditedEndpointName] = React.useState(''),
    { addToast } = useToast(),
    workspaceName = React.useMemo(() => `workspaces/${workspaceId}`, [workspaceId]),
    permissions = useGranularMLOpsPermissions(workspaceName),
    endpointsQuery = useQueryEndpoints(
      {
        workspaceId: workspaceId || ``,
      },
      {
        enabled: Boolean(workspaceId && permissions?.isAbleToViewEndpoints),
        meta: {
          toastError: (err: unknown) => `Failed to fetch endpoints: ${formatError(err)}`,
        },
        select: (data) => {
          const flattenedEndpoints = data.pages.flatMap((page) => page.endpoints || []);
          return { pages: flattenedEndpoints, pageParams: data.pageParams };
        },
      }
    ),
    endpoints = endpointsQuery.data?.pages || [],
    createEndpointMutation = useMutationCreateEndpoint({
      onError: (err: unknown) => {
        const message = `Failed to create endpoint: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        setCustomError(formatError(err));
        setShowValidation(true);
      },
      onSuccess: () => {
        addToast({
          messageBarType: MessageBarType.success,
          message: 'Endpoint created successfully.',
        });
        onDialogDismiss();
      },
    }),
    updateEndpointMutation = useMutationUpdateEndpoint({
      onError: (err: unknown) => {
        const message = `Failed to update endpoint: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      },
      onSuccess: () => {
        addToast({
          messageBarType: MessageBarType.success,
          message: 'Endpoint updated successfully.',
        });
      },
    }),
    deleteEndpointMutation = useMutationDeleteEndpoint({
      onError: (err: unknown) => {
        const message = `Failed to delete endpoint: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      },
      onSuccess: () => {
        addToast({
          messageBarType: MessageBarType.success,
          message: 'Endpoint deleted successfully.',
        });
      },
    }),
    onCreateEndpointAction = React.useCallback(() => {
      setDeploymentTarget(deploymentId || '');
      setDialogVisible(true);
    }, [deploymentId]),
    onDialogDismiss = () => {
      setDialogVisible(false);
      setDeploymentTarget('');
      setCustomEndpointPath('');
      setEndpointName('');
      setEndpointDescription('');
      setShowValidation(false);
      setCustomError(undefined);
      setCurrentlyEditedEndpointName('');
    },
    onEndpointCreate = () => {
      if (!customEndpointPath || !endpointName) {
        setShowValidation(true);
        return;
      }
      const endpoint = {
        description: endpointDescription,
        displayName: endpointName,
        path: customEndpointPath,
        target: `${workspaceName}/deployments/${deploymentTarget}`,
      };
      createEndpointMutation.mutate(endpoint);
    },
    onEndpointUpdate = () => {
      if (!customEndpointPath || !endpointName) {
        setShowValidation(true);
        return;
      }
      updateEndpointMutation.mutate({
        endpoint: {
          name: currentlyEditedEndpointName,
          displayName: endpointName,
          description: endpointDescription,
          path: customEndpointPath,
          // TODO: Add support for editing targets (deployments)?
        },
        updateMask: 'display_name,description',
      });
      onDialogDismiss();
    };

  React.useEffect(() => {
    if (!workspaceName) return;
    if (deploymentId && endpoints && scorerEndpoint) {
      // The hardcoded fallback value is for local mocks.
      const [urlPrefix, urlSuffix] = scorerEndpoint.includes(deploymentId)
        ? scorerEndpoint.split(deploymentId)
        : ['', ''];
      if (urlPrefix && !endpointPrefix) setEndpointPrefix(urlPrefix);
      if (urlSuffix && !endpointSuffix) setEndpointSuffix(urlSuffix);
      const endpointOptions: IDropdownOption[] = [];
      if (scorerEndpoint) {
        endpointOptions.push({
          key: scorerEndpoint,
          text: 'Scorer (default)',
          data: {
            name: 'scorer-endpoint',
          },
        });
      }
      if (endpoints?.length) {
        endpointOptions.push(
          ...endpoints
            .filter((endpoint) => endpoint.target?.includes(deploymentId))
            .map((endpoint) => ({
              key: `${urlPrefix}${endpoint.path}${urlSuffix}`,
              text: endpoint.displayName || `${urlPrefix}${endpoint.path}${urlSuffix}`,
              data: endpoint,
            }))
        );
      }
      setEndpointOptions(endpointOptions);
    }
  }, [deploymentId, scorerEndpoint, endpoints, workspaceName]);

  return (
    <div className={classNames.form}>
      <BaseDialog
        hidden={!dialogVisible}
        title={'Create new endpoint'}
        content={
          <>
            <p>If the default scorer endpoint is not suitable, you can create the endpoint with a custom path.</p>
            <TextField
              required
              label="Name"
              value={endpointName}
              disabled={
                currentlyEditedEndpointName ? !permissions?.canUpdateEndpoints : !permissions?.isAbleToCreateEndpoints
              }
              className={classNames.dialogInput}
              onChange={(_ev, newValue) => setEndpointName(newValue || '')}
              errorMessage={showValidation && !endpointName ? 'This field cannot be empty.' : ''}
            />
            <TextField
              label="Description"
              value={endpointDescription}
              disabled={
                currentlyEditedEndpointName ? !permissions?.canUpdateEndpoints : !permissions?.isAbleToCreateEndpoints
              }
              className={classNames.dialogInput}
              onChange={(_ev, newValue) => setEndpointDescription(newValue || '')}
            />
            <TextField
              required
              label="Path"
              value={customEndpointPath}
              disabled={Boolean(currentlyEditedEndpointName) || !permissions?.isAbleToCreateEndpoints}
              className={classNames.dialogInput}
              onChange={(_ev, newValue) => setCustomEndpointPath(newValue || '')}
              errorMessage={showValidation && !customEndpointPath ? 'This field cannot be empty.' : ''}
            />
            {customEndpointPath ? (
              <Info styles={{ text: { alignContent: 'center' } }}>
                <b>Endpoint URL preview: </b>
                {`${endpointPrefix}${customEndpointPath}${endpointSuffix}`}
              </Info>
            ) : null}
            {showValidation && customError ? (
              <Info styles={{ text: { alignContent: 'center', color: theme.semanticColors?.inputErrorMessageText } }}>
                <b>Error:</b>
                {customError}
              </Info>
            ) : null}
          </>
        }
        onDismiss={onDialogDismiss}
        footer={
          <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
            <Button onClick={onDialogDismiss} text="Cancel" />
            {permissions.isAbleToCreateEndpoints && !currentlyEditedEndpointName ? (
              <Button styles={buttonStylesPrimary} onClick={onEndpointCreate} text="Create endpoint" />
            ) : null}
            {permissions.canUpdateEndpoints && currentlyEditedEndpointName ? (
              <Button styles={buttonStylesPrimary} onClick={onEndpointUpdate} text="Update endpoint" />
            ) : null}
          </Stack>
        }
      />
      {endpointOptions.length
        ? endpointOptions.map((option) => (
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 10 }}
              key={`stack-${option.key}`}
              style={{ marginBottom: 10, maxWidth: 950 }}
            >
              <TextWithCopy
                key={option.key}
                hasBorder
                header={option.text}
                styles={[
                  textWithCopyStylesBorder,
                  {
                    header: { minWidth: 180 },
                    root: { flexGrow: 1, maxWidth: `calc(100% - ${permissions?.canUpdateEndpoints ? 120 : 60}px)` },
                  },
                ]}
                text={option.key as string}
              />
              {option.data?.name !== 'scorer-endpoint' && permissions?.canUpdateEndpoints ? (
                <IconButton
                  iconProps={{ iconName: 'Edit', style: { fontSize: 18 } }}
                  styles={buttonStylesStealth}
                  onClick={() => {
                    const endpoint = option.data as Endpoint;
                    setCurrentlyEditedEndpointName(endpoint.name || '');
                    setEndpointName(endpoint.displayName || '');
                    setEndpointDescription(endpoint.description || '');
                    setCustomEndpointPath(endpoint.path || '');
                    setDeploymentTarget(deploymentId || '');
                    setDialogVisible(true);
                  }}
                />
              ) : null}
              {option.data?.name !== 'scorer-endpoint' && permissions?.isAbleToDeleteEndpoints ? (
                <IconButton
                  iconProps={{ iconName: 'Delete', style: { fontSize: 18 } }}
                  styles={buttonStylesStealth}
                  onClick={() => {
                    showDialog?.({
                      title: 'Delete endpoint?',
                      onConfirm: () => {
                        deleteEndpointMutation.mutate(option.data.name || '');
                      },
                    });
                  }}
                />
              ) : null}
            </Stack>
          ))
        : isNew
        ? `A default scorer endpoint will be assigned to this deployment after creation.`
        : permissions.isAbleToViewEndpoints
        ? 'No endpoints available.'
        : 'You have no permissions to view endpoints. Ask the workspace owner to grant you the necessary permissions.'}
      {isNew || !permissions?.isAbleToCreateEndpoints ? null : (
        <div className={classNames.customEndpointButton}>
          <Button
            text="Add custom endpoint"
            iconName="Add"
            onClick={onCreateEndpointAction}
            styles={buttonStylesGhost}
          />
        </div>
      )}
    </div>
  );
};
